import React, { useState, useEffect } from 'react';
import { ProfileService, UserProfile } from '../services/profileService';
import { SponsorService, Sponsor, Sponsee, CreateSponsorRequest } from '../services/sponsorService';
import { UserProgramPreferencesService } from '../services/userProgramPreferencesService';
import { useDialog } from '../context/DialogContext';
import { parseDateSafe } from '../utils/dateUtils';
import InviteManagement from './InviteManagement';
import { DISPLAY_NAME_HINT_KEY, DISPLAY_NAME_LABEL_KEY, DISPLAY_NAME_PLACEHOLDER_KEY } from '../config/displayName';
import { useLocale } from '../context/LocaleContext';
import styles from '../styles/profileModal.module.css';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
  initialTab?: 'profile' | 'sponsorship';
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, user, initialTab = 'profile' }) => {
  const { showAlert, showConfirm } = useDialog();
  const { t } = useLocale();
  const [activeTab, setActiveTab] = useState<'profile' | 'sponsorship'>(initialTab);
  const [profileData, setProfileData] = useState<UserProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<UserProfile>>({});
  
  // Sponsor state
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [isLoadingSponsors, setIsLoadingSponsors] = useState(false);
  const [newSponsor, setNewSponsor] = useState<CreateSponsorRequest>({ program: '', name: '', email: '' });
  const [primaryProgram, setPrimaryProgram] = useState<string>('');
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Sponsee state
  const [sponsees, setSponsees] = useState<Sponsee[]>([]);
  const [isLoadingSponsees, setIsLoadingSponsees] = useState(false);
  
  // Invitation and progress sharing
  const [inviteManagementOpen, setInviteManagementOpen] = useState(false);
  const [invitePreselectedType, setInvitePreselectedType] = useState<'sponsor' | 'sponsee' | 'friend' | undefined>();

  // Load profile data
  useEffect(() => {
    if (isOpen && user) {
      setActiveTab(initialTab);
      loadProfileData();
      loadSponsors();
      loadSponsees();
      loadPrimaryProgram();
    }
  }, [isOpen, user, initialTab]);

  const loadProfileData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await ProfileService.getUserProfile();
      setProfileData(data);
      setEditForm({
        display_name: data.display_name,
        bio: data.bio,
        location: data.location,
        phone: data.phone
      });
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(t('account.profile.load_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      await ProfileService.updateUserProfile(editForm);
      if (profileData) {
        setProfileData({ ...profileData, ...editForm });
      }
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(t('account.profile.update_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    // Reset form to original values
    if (profileData) {
      setEditForm({
        display_name: profileData.display_name,
        bio: profileData.bio,
        location: profileData.location,
        phone: profileData.phone
      });
    }
  };

  // Sponsor management functions
  const loadSponsors = async () => {
    setIsLoadingSponsors(true);
    try {
      const sponsorList = await SponsorService.getSponsors();
      setSponsors(sponsorList);
    } catch (error) {
      console.error('Error loading sponsors:', error);
    } finally {
      setIsLoadingSponsors(false);
    }
  };

  const loadSponsees = async () => {
    setIsLoadingSponsees(true);
    try {
      const sponseeList = await SponsorService.getSponsees();
      setSponsees(sponseeList);
    } catch (error) {
      console.error('Error loading sponsees:', error);
    } finally {
      setIsLoadingSponsees(false);
    }
  };

  const loadPrimaryProgram = async () => {
    try {
      const preferences = await UserProgramPreferencesService.getUserProgramPreferences();
      const primary = preferences?.primaryProgram || 'saa';
      setPrimaryProgram(primary);
      // Set default program for new sponsor
      setNewSponsor(prev => ({ ...prev, program: primary.toUpperCase() }));
    } catch (error) {
      console.error('Error loading primary program:', error);
      setPrimaryProgram('saa');
      setNewSponsor(prev => ({ ...prev, program: 'SAA' }));
    }
  };

  const handleAddSponsor = async () => {
    // Validate
    if (!newSponsor.program || !newSponsor.name.trim() || !newSponsor.email.trim()) {
      showAlert({
        message: t('account.sponsor.fill_all_fields'),
        variant: 'warning'
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newSponsor.email)) {
      showAlert({
        message: t('account.sponsor.invalid_email'),
        variant: 'warning'
      });
      return;
    }

    try {
      await SponsorService.createSponsor(newSponsor);
      await loadSponsors();
      // Reset form and hide it
      setNewSponsor({ program: primaryProgram.toUpperCase(), name: '', email: '' });
      setShowAddForm(false);
      showAlert({
        message: t('account.sponsor.added'),
        variant: 'success'
      });
    } catch (error) {
      console.error('Error adding sponsor:', error);
      showAlert({
        message: t('account.sponsor.add_failed'),
        variant: 'danger'
      });
    }
  };

  const handleDeleteSponsor = (sponsor: Sponsor) => {
    showConfirm({
      message: t('account.sponsor.remove_confirm', { name: sponsor.name, email: sponsor.email, program: sponsor.program }),
      onConfirm: async () => {
        try {
          await SponsorService.deleteSponsor(sponsor.id);
          await loadSponsors();
          showAlert({
            message: t('account.sponsor.removed'),
            variant: 'success'
          });
        } catch (error) {
          console.error('Error deleting sponsor:', error);
          showAlert({
            message: t('account.sponsor.remove_failed'),
            variant: 'danger'
          });
        }
      },
      variant: 'danger'
    });
  };

  const handleDeleteSponsee = (sponsee: Sponsee) => {
    showConfirm({
      message: t('account.sponsee.remove_confirm', { name: sponsee.name, email: sponsee.email, program: sponsee.program }),
      onConfirm: async () => {
        try {
          await SponsorService.deleteSponsee(sponsee.id);
          await loadSponsees();
          showAlert({
            message: t('account.sponsee.removed'),
            variant: 'success'
          });
        } catch (error) {
          console.error('Error deleting sponsee:', error);
          showAlert({
            message: t('account.sponsee.remove_failed'),
            variant: 'danger'
          });
        }
      },
      variant: 'danger'
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleOpenInviteManagement = (relationshipType: 'sponsor' | 'sponsee' | 'friend') => {
    setInvitePreselectedType(relationshipType);
    setInviteManagementOpen(true);
  };

  const handleInviteManagementClose = () => {
    setInviteManagementOpen(false);
    // Reload sponsors/sponsees after closing (in case invitations were sent and accepted)
    loadSponsors();
    loadSponsees();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{t('account.title')}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.tabContainer}>
          <button
            className={`${styles.tabButton} ${activeTab === 'profile' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            {t('account.tab.profile')}
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'sponsorship' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('sponsorship')}
          >
            {t('account.tab.sponsorship')}
          </button>
        </div>

        <div className={styles.modalBody}>
          {activeTab === 'profile' && (
            <div className={styles.profileTab}>
              {isLoading ? (
                <div className={styles.loadingMessage}>{t('account.profile.loading')}</div>
              ) : error ? (
                <div className={styles.errorMessage}>{error}</div>
              ) : profileData ? (
                <div className={styles.profileContent}>
                  {isEditing ? (
                    <div className={styles.editForm}>
                      <div className={styles.profileField}>
                        <label className={styles.fieldLabel}>{t('account.field.email')}</label>
                        <input
                          type="email"
                          value={user?.email || ''}
                          className={styles.fieldInput}
                          readOnly
                          disabled
                        />
                      </div>
                      
                      <div className={styles.profileField}>
                        <label className={styles.fieldLabel}>{t(DISPLAY_NAME_LABEL_KEY)}</label>
                        <input
                          type="text"
                          value={editForm.display_name || ''}
                          onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                          className={styles.fieldInput}
                          placeholder={t(DISPLAY_NAME_PLACEHOLDER_KEY)}
                        />
                        <p className={styles.preferenceHelp}>{t(DISPLAY_NAME_HINT_KEY)}</p>
                      </div>

                      <div className={styles.profileField}>
                        <label className={styles.fieldLabel}>{t('account.field.phone')}</label>
                        <input
                          type="tel"
                          value={editForm.phone || ''}
                          onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                          className={styles.fieldInput}
                          placeholder={t('account.field.phone_placeholder')}
                          autoComplete="tel"
                        />
                        <p className={styles.preferenceHelp}>
                          {t('account.field.phone_help')}
                        </p>
                      </div>
                      
                      <div className={styles.profileActions}>
                        <button
                          className={styles.saveButton}
                          onClick={handleSave}
                          disabled={isLoading}
                        >
                          {isLoading ? t('account.profile.saving') : t('account.profile.save_changes')}
                        </button>
                        <button
                          className={styles.cancelButton}
                          onClick={handleCancel}
                          disabled={isLoading}
                        >
                          {t('dialog.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={styles.profileField}>
                        <label className={styles.fieldLabel}>{t('account.field.email')}</label>
                        <div className={styles.fieldValue}>{user?.email || t('account.field.not_available')}</div>
                      </div>

                      <div className={styles.profileFieldRow}>
                        <div className={styles.profileField}>
                          <label className={styles.fieldLabel}>{t(DISPLAY_NAME_LABEL_KEY)}</label>
                          <div className={styles.fieldValue}>{profileData.display_name || t('account.field.not_provided')}</div>
                        </div>

                        <div className={styles.profileField}>
                          <label className={styles.fieldLabel}>{t('account.field.phone')}</label>
                          <div className={styles.fieldValue}>
                            {profileData.phone?.trim() ? profileData.phone : t('account.field.not_provided')}
                          </div>
                        </div>
                      </div>

                      <div className={styles.profileFieldRow}>
                        <div className={styles.profileField}>
                          <label className={styles.fieldLabel}>{t('account.field.member_since')}</label>
                          <div className={styles.fieldValue}>
                            {profileData.member_since ? parseDateSafe(profileData.member_since).toLocaleDateString() : t('account.field.not_available')}
                          </div>
                        </div>

                        <div className={styles.profileField}>
                          <label className={styles.fieldLabel}>{t('account.field.last_sign_in')}</label>
                          <div className={styles.fieldValue}>
                            {profileData.last_login ? parseDateSafe(profileData.last_login).toLocaleDateString() : t('account.field.not_available')}
                          </div>
                        </div>
                      </div>

                      <div className={`${styles.profileActions} ${styles.profileActionsInline}`}>
                        <button
                          className={styles.editButton}
                          onClick={handleEdit}
                          disabled={isLoading}
                        >
                          {t('account.profile.edit')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className={styles.noDataMessage}>{t('account.profile.no_data')}</div>
              )}
            </div>
          )}

          {activeTab === 'sponsorship' && (
            <div className={styles.sponsorshipTab}>
              {/* MY SPONSORS SECTION */}
              <div className={styles.sponsorshipSection}>
                <div className={styles.sectionHeader}>
                  <div>
                    <h3 className={styles.sectionTitle}>{t('account.sponsor.section_title')}</h3>
                    <p className={styles.sectionDescription}>
                      {t('account.sponsor.section_description')}
                    </p>
                  </div>
                  <button 
                    onClick={() => handleOpenInviteManagement('sponsor')}
                    className={styles.inviteButton}
                  >
                    {t('account.sponsor.invite_button')}
                  </button>
                </div>

                {isLoadingSponsors ? (
                  <div className={styles.loading}>{t('account.sponsor.loading')}</div>
                ) : sponsors.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>{t('account.sponsor.empty')}</p>
                  </div>
                ) : (
                  <div className={styles.sponsorsList}>
                    {sponsors.map(sponsor => (
                      <div key={sponsor.id} className={styles.sponsorItem}>
                        <div className={styles.sponsorMainInfo}>
                          <div className={styles.sponsorHeader}>
                            <div className={styles.sponsorName}>{sponsor.name}</div>
                            <div className={styles.statusBadge} data-status={sponsor.confirmation_status}>
                              {sponsor.confirmation_status === 'confirmed' ? t('account.status.confirmed') : t('account.status.pending')}
                            </div>
                          </div>
                          <div className={styles.sponsorDetails}>
                            <span className={styles.sponsorProgram}>{sponsor.program}</span>
                            <span className={styles.sponsorEmail}>{sponsor.email}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteSponsor(sponsor)}
                          className={styles.deleteButton}
                          title={t('account.sponsor.remove_title')}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* MY SPONSEES SECTION */}
              <div className={styles.sponsorshipSection}>
                <div className={styles.sectionHeader}>
                  <div>
                    <h3 className={styles.sectionTitle}>{t('account.sponsee.section_title')}</h3>
                    <p className={styles.sectionDescription}>
                      {t('account.sponsee.section_description')}
                    </p>
                  </div>
                  <button 
                    onClick={() => handleOpenInviteManagement('sponsee')}
                    className={styles.inviteButton}
                  >
                    {t('account.sponsee.invite_button')}
                  </button>
                </div>

                {isLoadingSponsees ? (
                  <div className={styles.loading}>{t('account.sponsee.loading')}</div>
                ) : sponsees.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>{t('account.sponsee.empty')}</p>
                  </div>
                ) : (
                  <div className={styles.sponsorsList}>
                    {sponsees.map(sponsee => (
                      <div key={sponsee.id} className={styles.sponsorItem}>
                        <div className={styles.sponsorMainInfo}>
                          <div className={styles.sponsorHeader}>
                            <div className={styles.sponsorName}>{sponsee.name}</div>
                            <div className={styles.statusBadge} data-status={sponsee.confirmation_status}>
                              {sponsee.confirmation_status === 'confirmed' ? t('account.status.confirmed') : t('account.status.pending')}
                            </div>
                          </div>
                          <div className={styles.sponsorDetails}>
                            <span className={styles.sponsorProgram}>{sponsee.program}</span>
                            <span className={styles.sponsorEmail}>{sponsee.email}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteSponsee(sponsee)}
                          className={styles.deleteButton}
                          title={t('account.sponsee.remove_title')}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Invite Management (send + history) */}
      <InviteManagement 
        isOpen={inviteManagementOpen}
        onClose={handleInviteManagementClose}
        preselectedRelationship={invitePreselectedType}
        preselectedProgram={primaryProgram.toUpperCase()}
      />
    </div>
  );
};

export default ProfileModal; 